// @ts-strict-ignore
import React from 'react'
import { actionAtom } from 'atoms/atoms'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Icon } from '@node-space/ui-components'
import { RequestError } from 'components/Errors/RequestError'
import { ExternalLink } from 'components/ExternalLink/ExternalLink'
import { Approval, PathNames } from 'constants/General'
import { ExternalLinks } from 'constants/links'
import { useMassCryptoPayoutsMutation } from 'hooks/mutations/useMassCryptoPayoutsMutation'
import { useMassFiatPayoutsMutation } from 'hooks/mutations/useMassFiatPayoutsMutation'
import { useApprovalsConfigForFlow } from 'hooks/useApprovalsConfigForFlow'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { BaseErrorResponse } from 'types/beneficiaries'
import { WalletTypes } from 'types/types'
import {
  fiatBatchReferenceAtom,
  fileAtom,
  massPayoutStepAtom,
  merchantAtom,
  totalNumberOfPayoutErrorsAtom,
  totalNumberOfPayoutsAtom,
  uploadErrorAtom,
  walletAtom,
  walletTypeAtom,
} from '../atoms/massPayoutAtoms'
import { MassPayoutSteps } from '../types'

export const MassPayoutSubmitButton = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { approvalsIsRequiredForFlow, isFetchingApprovalPreference } = useApprovalsConfigForFlow(
    Approval.FLOWS.CRYPTO_PAYOUT
  )
  const permissions = useMappedRoles()
  const canEdit = permissions?.massPayouts?.canEdit

  const [merchant, setMerchant] = useAtom(merchantAtom)
  const [wallet, setWallet] = useAtom(walletAtom)
  const [file, setFile] = useAtom(fileAtom)
  const [error, setError] = useAtom(uploadErrorAtom)

  const setFiatBatchReference = useSetAtom(fiatBatchReferenceAtom)
  const action = useAtomValue(actionAtom)
  const setMassPayoutStep = useSetAtom(massPayoutStepAtom)
  const setTotalNumberOfPayouts = useSetAtom(totalNumberOfPayoutsAtom)
  const setTotalNumberOfPayoutErrors = useSetAtom(totalNumberOfPayoutErrorsAtom)

  const walletType = useAtomValue(walletTypeAtom)

  const { mutate: massCryptoPayoutsMutation, isPending: isMassCryptoPayoutsLoading } =
    useMassCryptoPayoutsMutation()

  const { mutate: massFiatPayoutsMutation, isPending: isMassFiatPayoutsLoading } =
    useMassFiatPayoutsMutation()

  const handleOnSuccess = response => {
    if (walletType === WalletTypes.FIAT) {
      setError('')
      setFile(null)
      setFiatBatchReference(response)
      setWallet('')
      navigate(`${PathNames.MASS_PAYOUTS}/payout-details/${response}?batchType=${walletType}`)
      action?.onClose()
    }
    if (walletType === WalletTypes.CRYPTO) {
      setTotalNumberOfPayouts(response?.totalItems || 0)
      setTotalNumberOfPayoutErrors(response?.totalErrors || 0)
      if (response?.totalErrors === 0) {
        if (approvalsIsRequiredForFlow) {
          setMassPayoutStep(MassPayoutSteps.APPROVAL)
        } else {
          setMassPayoutStep(MassPayoutSteps.SUCCESS)
        }
      } else {
        action?.onClose()
        navigate(
          `${PathNames.MASS_PAYOUTS}/payout-details/${response?.externalId}?batchType=${walletType}`
        )
      }

      setError('')
      setFile(null)
      setMerchant('')
      setWallet('')
    }
  }

  const contactSupportTextAction = (
    <ExternalLink href={ExternalLinks.CONTACT_SUPPORT}>
      {t('errorMessages.contactSupport')}
    </ExternalLink>
  )

  const handleOnError = (error: BaseErrorResponse) => {
    const bvknErrorCode = error?.data?.code
    const errorMessage = error?.data?.message
    const errorListMessage = error?.errorList?.[0]?.message
    if (bvknErrorCode === 'BVNK-FIAT-0051') {
      const submitFileError = (
        <>
          {t('massPayouts.fiatErrors.submitFileError')} {contactSupportTextAction}.
        </>
      )
      setError(submitFileError)
    } else {
      setError(errorListMessage || errorMessage || t('oopsSomethingWentWrong'))
    }
  }

  const handleSubmit = () => {
    const mutation = {
      onSuccess: handleOnSuccess,
      onError: handleOnError,
    }

    if (walletType === WalletTypes.FIAT) {
      massFiatPayoutsMutation(
        {
          walletId: wallet as string,
          file: file,
        },
        mutation
      )
    }

    if (walletType === WalletTypes.CRYPTO) {
      massCryptoPayoutsMutation(
        {
          walletName: wallet as string,
          walletId: merchant,
          file: file,
        },
        mutation
      )
    }
  }

  const isMassPayoutDisabled =
    walletType === WalletTypes.FIAT ? !file || !wallet || !!error : !file || !merchant || !!error

  return (
    <>
      {!!error && <RequestError>{error}</RequestError>}
      <Box flex justify="end">
        <Button
          loading={
            isMassCryptoPayoutsLoading || isMassFiatPayoutsLoading || isFetchingApprovalPreference
          }
          disabled={isMassPayoutDisabled || !canEdit}
          className="-mr-2"
          iconElement={<Icon name="PaperPlane" />}
          onClick={handleSubmit}
        >
          {t('massPayouts.submitFile')}
        </Button>
      </Box>
    </>
  )
}
